'use client';

import Image from 'next/image';
import { cn } from '@/lib/utils';

interface TakeoverIconProps {
  size?: number;
  className?: string;
  /** If true, draws the "T" glyph on the chip. */
  glyph?: boolean;
}

/**
 * 3D-style Takeover chip: angled gold tile with chiseled bevel, specular
 * highlight, and an inlaid "T" glyph. Used to replace the flat `T` badge in
 * the header and anywhere we stamp the brand.
 */
export function TakeoverIcon({ size = 18, className, glyph = true }: TakeoverIconProps) {
  const uid = `tk-${size}`;
  return (
    <svg
      viewBox="0 0 32 32"
      width={size}
      height={size}
      className={cn('shrink-0 drop-shadow-[0_1px_0_rgba(0,0,0,0.6)]', className)}
      aria-hidden
    >
      <defs>
        <linearGradient id={`${uid}-body`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#fff7c7" />
          <stop offset="32%" stopColor="#f6c961" />
          <stop offset="72%" stopColor="#b4741d" />
          <stop offset="100%" stopColor="#5b3205" />
        </linearGradient>
        <linearGradient id={`${uid}-top`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffe9a1" />
          <stop offset="55%" stopColor="#f0b34a" />
          <stop offset="100%" stopColor="#a65f0e" />
        </linearGradient>
        <linearGradient id={`${uid}-edge`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#8a4e0c" />
          <stop offset="100%" stopColor="#3a1e03" />
        </linearGradient>
        <radialGradient id={`${uid}-spec`} cx="0.25" cy="0.2" r="0.6">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
          <stop offset="55%" stopColor="#ffffff" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
      </defs>

      <g transform="translate(2.5 3.5)">
        <path
          d="M3 3 L22 1 L26 4.5 L7 6.5 Z"
          fill={`url(#${uid}-top)`}
        />
        <path
          d="M3 3 L7 6.5 L7 22 L3 18 Z"
          fill={`url(#${uid}-edge)`}
        />
        <path
          d="M7 6.5 L26 4.5 L26 20.5 L7 22 Z"
          fill={`url(#${uid}-body)`}
          stroke="#2a1504"
          strokeWidth="0.6"
          strokeLinejoin="round"
        />

        <path
          d="M7.5 7.2 L25.4 5.3 L25.4 8.3 L7.5 9.8 Z"
          fill={`url(#${uid}-spec)`}
          opacity="0.85"
        />

        {glyph && (
          <g transform="translate(7.5 6.5)">
            <path
              d="M2 3.2 L16 2 L16 5 L11 5.4 L10.4 17.2 L7 17.5 L7.5 5.6 L2 6.1 Z"
              fill="#2a1504"
              opacity="0.9"
            />
            <path
              d="M2 2.8 L16 1.6 L16 4.6 L11 5 L10.4 16.8 L7 17.1 L7.5 5.2 L2 5.7 Z"
              fill="#fff4c2"
              opacity="0.85"
            />
          </g>
        )}

        <path
          d="M7 22 L26 20.5 L26 21.8 L7 23.2 Z"
          fill="#1a0d02"
          opacity="0.7"
        />
      </g>
    </svg>
  );
}

/**
 * Inline "takeover.fun" wordmark tuned for the lobby's "nouveau
 * sci-fi" direction. We use `Chakra Petch 700` — a modern display sans
 * with angled terminals and a geometric skeleton — so the wordmark
 * reads like the HUD of a near-future game lobby rather than retro
 * pixel or medieval calligraphy.
 *
 * Colours are untouched: `takeover` stays amber-50 and `.fun` keeps the
 * shared `text-brand-red` token so it matches the classic theme. The
 * subtle gold text-shadow ties it back to the gilded nav chrome.
 *
 * Vertical alignment: Chakra Petch has normal sans metrics (balanced
 * ascender/descender budget), so `items-center` with `leading-none` is
 * enough — no custom line-height pinning like we needed for the
 * calligraphic medieval cut.
 */
export function TakeoverWordmark({
  size = 11,
  className,
  tight = false,
}: {
  size?: number;
  className?: string;
  /** Kept for API compatibility — no longer used now the mark is lowercase. */
  tight?: boolean;
}) {
  // Chakra Petch has generous x-height and cap-height — 1.25× the
  // consumer's base size lands us at roughly the same optical footprint
  // as the old JetBrains Mono wordmark without over-bulking the logo.
  const fontSize = Math.round(size * 1.25);
  // Mascot chip sized slightly above cap-height so the pixel mascot
  // reads as the visual anchor and the text rides cleanly alongside it.
  const iconSize = Math.round(size * 1.4);
  return (
    <span
      className={cn(
        // items-center keeps the mascot chip and the text on the same
        // cross-axis centre line. leading-none kills any stray line-box
        // padding. select-none prevents drag-select on the logo during
        // scene interaction.
        'inline-flex items-center font-display font-bold leading-none select-none',
        className,
      )}
      style={{ fontSize: `${fontSize}px` }}
    >
      <span
        className="relative inline-block shrink-0 mr-1.5 overflow-hidden rounded-[2px] border border-amber-500/40 shadow-[0_0_4px_rgba(255,215,120,0.35)]"
        style={{
          width: `${iconSize}px`,
          height: `${iconSize}px`,
        }}
        aria-hidden
      >
        <Image
          src="/mascot.png"
          alt=""
          fill
          sizes={`${iconSize}px`}
          className="object-cover"
          style={{ imageRendering: 'pixelated' }}
        />
      </span>
      <span
        className="text-amber-50 lowercase inline-flex items-center"
        style={{
          // Pinned line-height keeps the glyph centre aligned with the
          // mascot chip centre regardless of the parent's computed
          // line-box — the flex row's intrinsic height is otherwise
          // driven by whichever child is taller.
          //
          // NOTE: letter-spacing is deliberately NOT set inline — the
          // footer overrides via `tracking-[0.3em]` on className and we
          // want that path to keep working. Chakra Petch reads fine at
          // 0 tracking for the header.
          lineHeight: `${iconSize}px`,
          textShadow:
            '0 0 10px rgba(255,215,120,0.22), 0 1px 0 rgba(0,0,0,0.7)',
        }}
      >
        takeover<span className="text-brand-red">.fun</span>
      </span>
    </span>
  );
}
